import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Set<string>} */
  #archivedVariantIds = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    this.#refreshArchivedVariantIds();
    this.#redirectIfArchivedVariantInUrl();
  }

  /**
   * Reads the archived variant id list rendered by the Liquid template.
   */
  #refreshArchivedVariantIds() {
    const script = this.querySelector('script[data-archived-variant-ids]');
    if (!script?.textContent) {
      this.#archivedVariantIds = new Set();
      return;
    }

    try {
      const ids = JSON.parse(script.textContent);
      this.#archivedVariantIds = new Set(Array.isArray(ids) ? ids.map(String) : []);
    } catch {
      this.#archivedVariantIds = new Set();
    }
  }

  /**
   * If the current URL points to an archived variant on the main PDP, switch to the first
   * visible available option so the buy buttons don't flash for an unsellable variant.
   */
  #redirectIfArchivedVariantInUrl() {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (this.#archivedVariantIds.size === 0) return;

    const currentVariantId = new URL(window.location.href).searchParams.get('variant');
    if (!currentVariantId || !this.#archivedVariantIds.has(currentVariantId)) return;

    const fallbackInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(
        'fieldset:not([data-product-group-picker] *) input[data-option-available="true"]:not([data-variant-id=""])'
      )
    );

    if (fallbackInput) {
      fallbackInput.checked = true;
      fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const fallbackOption = /** @type {HTMLOptionElement | null} */ (
      this.querySelector(
        'select:not([data-product-group-select]) option[data-option-available="true"]:not([data-variant-id=""])'
      )
    );

    if (fallbackOption?.parentElement instanceof HTMLSelectElement) {
      const select = fallbackOption.parentElement;
      select.value = fallbackOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target instanceof HTMLSelectElement &&
      event.target.hasAttribute('data-product-group-select')
    ) {
      const url = event.target.value;
      if (url) window.location.href = url;
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    this.#refreshArchivedVariantIds();
    this.#autoReselectUnavailableOptions();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset:not([data-product-group-picker] *) input:checked')
    );
    if (selectedInput instanceof HTMLInputElement) return selectedInput;

    const selectedOption = this.querySelector('select:not([data-product-group-select]) option[selected]');

    if (!(selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * For product-group PDPs, if any checked variant input is OOS after a morph,
   * auto-selects the first available option in that fieldset and re-fetches.
   * Only runs when the picker has `variant-picker--has-product-group`.
   */
  #autoReselectUnavailableOptions() {
    if (!this.classList.contains('variant-picker--has-product-group')) return;

    let changed = false;

    for (const fieldset of this.querySelectorAll('fieldset:not([data-product-group-picker] *)')) {
      const checkedInput = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));

      const checkedIsArchived =
        checkedInput && this.#archivedVariantIds.has(checkedInput.dataset.variantId ?? '');

      // Only jump off options that are hidden (non-existent combination) or archived.
      // Sold-out-but-real options are intentionally visible and selectable, so we leave them.
      const checkedDoesNotExist = checkedInput && checkedInput.dataset.optionExists === 'false';

      if (!checkedInput || (!checkedDoesNotExist && !checkedIsArchived)) continue;

      /** @type {HTMLInputElement | null} */
      let fallback = null;
      // Prefer an in-stock, non-archived option.
      for (const candidate of /** @type {NodeListOf<HTMLInputElement>} */ (
        fieldset.querySelectorAll('input[data-option-available="true"]')
      )) {
        if (this.#archivedVariantIds.has(candidate.dataset.variantId ?? '')) continue;
        fallback = candidate;
        break;
      }
      // Otherwise fall back to the first existing (real), non-archived option, even if sold out.
      if (!fallback) {
        for (const candidate of /** @type {NodeListOf<HTMLInputElement>} */ (
          fieldset.querySelectorAll('input[data-option-exists="true"]')
        )) {
          if (this.#archivedVariantIds.has(candidate.dataset.variantId ?? '')) continue;
          fallback = candidate;
          break;
        }
      }

      if (!fallback) continue;

      checkedInput.checked = false;
      fallback.checked = true;
      changed = true;
    }

    if (!changed) return;

    const anchorInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset:not([data-product-group-picker] *) input:checked')
    );

    if (anchorInput) {
      this.fetchUpdatedSection(this.buildRequestUrl(anchorInput));
    }
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select:not([data-product-group-select]) option[selected], fieldset:not([data-product-group-picker] *) input:checked'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
